import {BrowserMultiFormatReader, NotFoundException} from '@zxing/library';
import React, {useEffect, useRef, useState} from 'react';
import Modal from 'react-bootstrap/Modal';
import {toast} from 'react-toastify';
import Button from '../Button';
import './styles.scss';

interface BarcodeScannerProps {
    show: boolean;
    onClose: () => void;
    onScan: (barcode: string) => void;
    expectedLength?: number;
}

const BarcodeScanner: React.FC<BarcodeScannerProps> = ({
    show,
    onClose,
    onScan,
    expectedLength = 44
}) => {
    const [scanning, setScanning] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const codeReaderRef = useRef<BrowserMultiFormatReader | null>(null);

    const stopScanner = async () => {
        if (codeReaderRef.current) {
            codeReaderRef.current.reset();
        }
        setScanning(false);
    };

    const startScanner = async () => {
        try {
            if (!codeReaderRef.current) {
                codeReaderRef.current = new BrowserMultiFormatReader();
            }

            const videoInputDevices = await codeReaderRef.current.listVideoInputDevices();

            if (videoInputDevices.length === 0) {
                toast.error('Nenhuma câmera encontrada no dispositivo.');
                onClose();
                return;
            }

            // Seleciona a câmera traseira preferencialmente
            const selectedDevice =
                videoInputDevices.find(
                    (device) =>
                        device.label.toLowerCase().includes('back') ||
                        device.label.toLowerCase().includes('traseira') ||
                        device.label.toLowerCase().includes('rear')
                ) || videoInputDevices[0];

            if (videoRef.current) {
                await codeReaderRef.current.decodeFromVideoDevice(
                    selectedDevice.deviceId,
                    videoRef.current,
                    (result, error) => {
                        if (result) {
                            const decodedText = result.getText();
                            // Remove espaços e caracteres especiais
                            const cleanedBarcode = decodedText.replace(
                                /\s/g,
                                ''
                            );

                            if (cleanedBarcode.length === expectedLength) {
                                onScan(cleanedBarcode);
                                stopScanner();
                                onClose();
                                toast.success(
                                    'Código de barras lido com sucesso!'
                                );
                            } else {
                                toast.warning(
                                    `Código deve ter ${expectedLength} caracteres. Lido: ${cleanedBarcode.length}`
                                );
                            }
                        }

                        if (error && !(error instanceof NotFoundException)) {
                            // Ignora NotFoundException que é normal durante o scan
                            console.log('Erro de scanner:', error);
                        }
                    }
                );

                setScanning(true);
            }
        } catch (error: any) {
            console.error('Erro ao iniciar scanner:', error);

            let errorMessage = 'Erro ao acessar a câmera.';

            if (error.name === 'NotReadableError') {
                errorMessage =
                    'Câmera já está em uso por outro aplicativo. Feche outros apps que possam estar usando a câmera.';
            } else if (error.name === 'NotAllowedError') {
                errorMessage =
                    'Permissão de câmera negada. Habilite o acesso à câmera nas configurações do navegador.';
            } else if (error.name === 'NotFoundError') {
                errorMessage = 'Nenhuma câmera encontrada no dispositivo.';
            } else if (error.name === 'NotSupportedError') {
                errorMessage =
                    'Acesso à câmera não suportado. Use HTTPS ou localhost.';
            } else if (error.message) {
                errorMessage = error.message;
            }

            toast.error(errorMessage);
            onClose();
        }
    };

    useEffect(() => {
        if (show) {
            // Pequeno delay para garantir que o elemento DOM está renderizado
            setTimeout(() => {
                startScanner();
            }, 100);
        }

        return () => {
            stopScanner();
        };
    }, [show]);

    const handleClose = () => {
        stopScanner();
        onClose();
    };

    return (
        <Modal show={show} onHide={handleClose} dialogAs="div" centered>
            <Modal.Dialog scrollable size="lg" centered>
                <Modal.Header className="bg-primary">
                    <Modal.Title>Scanner de Código de Barras</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="scanner-container">
                        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                        <video
                            ref={videoRef}
                            id="barcode-scanner-video"
                            className="scanner-video"
                        />
                        {scanning && (
                            <div className="scanner-instructions">
                                <p>
                                    Posicione o código de barras dentro da área
                                    destacada
                                </p>
                                <p className="text-muted">
                                    Esperando código com {expectedLength}{' '}
                                    caracteres
                                </p>
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        type="button"
                        color="danger"
                        size="btn btn-default"
                        icon="fa fa-times"
                        caption="Cancelar"
                        onClick={handleClose}
                    />
                </Modal.Footer>
            </Modal.Dialog>
        </Modal>
    );
};

export default BarcodeScanner;
